import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Compact, design-token-styled Markdown for the chat transcript. The agent
 * streams GitHub-flavored Markdown; render it instead of showing raw `**`/`##`.
 * react-markdown does not interpret raw HTML (no `rehype-raw`), so this is
 * injection-safe. Element styles stay tight to match the calm transcript voice.
 */
const components: Components = {
  p: ({ children }) => <p className="leading-relaxed my-2 first:mt-0 last:mb-0">{children}</p>,
  h1: ({ children }) => <h1 className="text-[15px] font-semibold mt-3 mb-1 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-[14px] font-semibold mt-3 mb-1 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-[13px] font-semibold mt-2.5 mb-1 first:mt-0">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-[var(--color-accent)] hover:opacity-80"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[var(--color-border)] pl-2.5 my-2 text-[var(--color-text-muted)]">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-[var(--color-border)]" />,
  code: ({ className, children }) => {
    // Fenced blocks carry a language- class; inline code does not.
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className="block font-mono text-[12px] whitespace-pre overflow-auto p-2.5 rounded-[6px] bg-[var(--color-surface-2)]">
          {children}
        </code>
      );
    }
    return (
      <code className="font-mono text-[0.92em] px-1 py-px rounded bg-[var(--color-surface-2)]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-2 overflow-auto">{children}</pre>,
  table: ({ children }) => (
    <div className="my-2 overflow-auto">
      <table className="border-collapse text-[12px]">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-[var(--color-border)] px-2 py-1 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-[var(--color-border)] px-2 py-1">{children}</td>,
};

export function Markdown({ text }: { text: string }) {
  return (
    <div className="cf-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
